// design/AuditLog.dc.html. FR-AD3: filterable, exportable as CSV/JSON. audit.rs serves both
// from the same GET, switched by `?format=csv` — the CSV branch downloads directly rather than
// this page assembling one client-side, so the export is exactly what the filtered view shows.
import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Card, Select } from "@grackle/ui";
import { api, ApiFail } from "../api/client";
import type { AuditEntryOut, ListResponse } from "../api/types";
import { Notice } from "../components/ui";
import { EVENT_OPTIONS } from "./auditEvents";
import type { AdminOutletContext } from "./AdminLayout";

// Issue #12: long detail payloads collapse behind a native disclosure.
function AuditDetail({ detail }: { detail: Record<string, unknown> }) {
  const json = JSON.stringify(detail);
  if (json.length <= 80) return <div className="admin-detail">{json}</div>;
  return (
    <details className="admin-detail">
      <summary>Details ({json.length} chars)</summary>
      <pre>{json}</pre>
    </details>
  );
}

function clock(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function AuditLogPage() {
  const { org } = useOutletContext<AdminOutletContext>();
  const [items, setItems] = useState<AuditEntryOut[]>([]);
  const [event, setEvent] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (event) params.set("event", event);
    const qs = params.toString();
    api<ListResponse<AuditEntryOut>>("GET", `/orgs/${org.id}/audit-log${qs ? `?${qs}` : ""}`)
      .then((res) => setItems(res.items))
      .catch((err) => setError(err instanceof ApiFail ? err.message : "Couldn't load the audit log."))
      .finally(() => setLoading(false));
  }, [org.id, event]);

  useEffect(() => {
    load();
  }, [load]);

  function exportHref(): string {
    const params = new URLSearchParams({ format: "csv" });
    if (event) params.set("event", event);
    return `/api/v1/orgs/${org.id}/audit-log?${params.toString()}`;
  }

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Audit log</h1>
          <p>Security-relevant events in {org.name} — most recent first.</p>
        </div>
      </header>

      <div className="admin-toolbar">
        <Select aria-label="Event type" value={event} onChange={setEvent} options={EVENT_OPTIONS} />
        <a className="gk-btn gk-btn--secondary gk-btn--sm admin-toolbar__end" href={exportHref()} target="_blank" rel="noreferrer">
          Export CSV
        </a>
      </div>

      {error ? <Notice tone="error">{error}</Notice> : null}

      {loading ? (
        <div className="admin-row-sub">Loading…</div>
      ) : items.length === 0 ? (
        <div className="admin-row-sub">No matching events yet.</div>
      ) : (
        <Card size="flush" className="admin-table-card">
<table className="admin-table">
          <thead>
            <tr>
              <th className="gk-overline">When</th>
              <th className="gk-overline">Actor</th>
              <th className="gk-overline">Event</th>
              <th className="gk-overline">Target</th>
              <th className="gk-overline">Detail</th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.id}>
                <td className="mono admin-nowrap">{clock(e.created_at)}</td>
                <td>{e.actor_email}</td>
                <td className="mono">{e.event}</td>
                <td className="admin-row-sub">
                  {e.target_type ? `${e.target_type}${e.target_id ? ` · ${e.target_id.slice(0, 8)}` : ""}` : "—"}
                </td>
                <td>
                  <AuditDetail detail={e.detail} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
</Card>
      )}
    </>
  );
}
